/**
 * @file order-detail-status-alerts.tsx
 * @module app/modules/orders-delivery/components/order-details/order-detail-status-alerts
 * @description Displays status alerts for an order, including payment and cancellation notifications.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.1
 * @version 2.1.2
 */

import Notification from "../../../../notification/notification";
import Method from "../../../../../utils/methods";
import {
  OrderCancelled,
  OrderDelivered,
} from "../../../../../utils/constants";
import { OrderDetails } from "../../types/order-details";

interface OrderDetailStatusAlertsProps {
  details: OrderDetails;
}

const OrderDetailStatusAlerts = ({ details }: OrderDetailStatusAlertsProps) => {
  if (!details) {
    return null;
  }

  return (
    <>
      {details.status === OrderDelivered ? (
        <>
          {!details.payment.receivedCharge ? (
            <>
              {details.payment.remainingCharge ? (
                <Notification
                  bgColor="bg-light"
                  textColor="dark"
                  text={
                    details.payment.receivedCharge
                      ? `The order has been paid partially. You have to take TSh ${Method.getGeneralizedAmount(
                          details.payment.remainingCharge,
                        )} from the customer.`
                      : `The entire payment is due the customer will pay later`
                  }
                />
              ) : null}
            </>
          ) : (
            <>
              {details.payment.transactions.length &&
              details.routesVehicles.some((v: any) => v.paymentCollection.collectedPartially) ? (
                <>
                  {details.payment.remainingCharge ? (
                    <Notification
                      bgColor="bg-light"
                      textColor="dark"
                      text={`The order has been paid partially. You have to take TSh ${Method.getGeneralizedAmount(
                        details.payment.remainingCharge,
                      )} from the customer.`}
                    />
                  ) : null}
                </>
              ) : (
                <>
                  {details.instantOrder ? (
                    <>
                      {details.payment.remainingCharge ? (
                        <Notification
                          bgColor="bg-light"
                          textColor="dark"
                          text={`The order has been paid partially. You have to take TSh ${Method.getGeneralizedAmount(
                            details.payment.remainingCharge,
                          )} from the customer.`}
                        />
                      ) : null}
                    </>
                  ) : null}
                </>
              )}
            </>
          )}
        </>
      ) : null}

      {details.payment.adjusted === true ? (
        <>
          {details.status === OrderCancelled ? (
            <>
              {(details.payment.receivedCharge ?? 0) > 0 ? (
                <Notification
                  bgColor="bg-light"
                  textColor="dark"
                  text={`Order has been cancelled. You had refunded TSh ${Method.getGeneralizedAmount(
                    details.payment.receivedCharge,
                  )} to the customer.`}
                />
              ) : null}
            </>
          ) : (
            <Notification
              bgColor="bg-light-success"
              textColor="dark"
              text={`Order has been modified. You had refunded TSh ${Method.getGeneralizedAmount(
                details.payment.adjustmentCharge,
              )} to the customer.`}
            />
          )}
        </>
      ) : (
        <>
          {details.status === OrderCancelled ? (
            <>
              {(details.payment.receivedCharge ?? 0) > 0 ? (
                <Notification
                  bgColor="bg-light"
                  textColor="dark"
                  text={`Order has been cancelled. You have to refund TSh ${Method.getGeneralizedAmount(
                    details.payment.receivedCharge,
                  )} to the customer.`}
                />
              ) : null}
            </>
          ) : (
            <>
              {(details.payment.adjustmentCharge ?? 0) > 0 ? (
                <Notification
                  bgColor="bg-light-success"
                  textColor="dark"
                  text={`Order has been modified.You have to refund TSh ${Method.getGeneralizedAmount(
                    details.payment.adjustmentCharge,
                  )} to the customer.`}
                />
              ) : null}
            </>
          )}
        </>
      )}
    </>
  );
};

export default OrderDetailStatusAlerts;
